import { Add } from "@mui/icons-material";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  LinearProgress,
  MenuItem,
  Fab,
  Checkbox,
  FormControlLabel,
  Box,
  Typography,
  Grid,
  ToggleButton,
  ToggleButtonGroup,
  Chip
} from "@mui/material";
import { PremiumTextField, PremiumOutlinedInput, PremiumFormControl } from "../PremiumInputs";
import { useState } from "react";
import * as yup from "yup";
import { Formik } from "formik";
import { addAccount, addEntry } from "../../firebase/dataHandler";
import * as moment from 'moment'
import { useToast } from "../../context/ToastContext";
import { useNavigate } from "react-router-dom";


function AddAccountForm({buttonStyle, refresh, customer, buttonText, isFab}) {
  const [open, setOpen] = useState(false);
  const [isLoading, setLoading] = useState(false);
  const { showToast } = useToast();

  const navigate = useNavigate()

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const validationSchema = yup.object({
    name: yup.string().required().min(3),
  });

  const formValues = {
    name: customer.name,
    amount: "",
    interestPercentage: "",
    interestAmount: "",
    contact: customer.contact,
    date: moment(new Date()).format("yyyy-MM-DD"),
    timeStamp : new Date().toString(),
    installmentType: "daily",
    installmentPeriod: 100,
    installmentAmount : 0,
    collectUpfrontInterest: false
  };

  return (
    <div>
      {isFab ? (
        <Fab color="primary" variant="extended" style={buttonStyle} onClick={handleClickOpen}>
          <Add sx={{ mr: 1 }} /> {buttonText || 'Account'}
        </Fab>
      ) : buttonText ? (
        <Button
          variant="contained"
          style={buttonStyle}
          size="small"
          color="primary"
          onClick={handleClickOpen}
          startIcon={<Add/>}
        >
          {buttonText}
        </Button>
      ) : (
        <IconButton style={buttonStyle} size="small" color="primary" onClick={handleClickOpen}>
          <Add />
        </IconButton>
      )}
      <Dialog 
        open={open} 
        onClose={handleClose} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          style: {
            borderRadius: 16,
            background: 'var(--bg-card)',
            border: '1px solid var(--border-subtle)',
          }
        }}
      >
        <LinearProgress hidden={!isLoading} sx={{ height: 3, '& .MuiLinearProgress-bar': { background: 'linear-gradient(90deg, #15C358, #109441)' } }} />
        <DialogTitle sx={{ pb: 1, borderBottom: '1px solid var(--border-subtle)', mb: 2 }}>
          <Typography sx={{ fontWeight: 800, fontSize: 18, color: 'var(--text-primary)', fontFamily: 'Inter' }}>
            Add New Account
          </Typography>
          <Typography sx={{ fontSize: 13, color: 'var(--text-muted)', fontFamily: 'Inter' }}>
            Create a loan account for {customer?.name}
          </Typography>
        </DialogTitle>
        
        <DialogContent sx={{ p: 3 }}>
          <Formik
            initialValues={formValues}
            validationSchema={validationSchema}
            onSubmit={(values, actions) => {
              setLoading(true)
              addAccount(customer.clientId, values).then(async (result)=>{
                if(values.collectUpfrontInterest && values.interestAmount > 0) {
                  await addEntry(result.accountId, {
                    date: values.date,
                    amountPaid: values.interestAmount,
                    dayCount: 0,
                    isUpfrontInterest: true,
                    timestamp: new Date().toString()
                  });
                }
                actions.setSubmitting(false)
                setLoading(false)
                actions.resetForm()
                showToast("Account created successfully", "success");
                refresh && refresh()
                navigate(`/account/${result.accountId}`)
                handleClose()
              }).catch(err=>{
                showToast(err, "error");
              })
            }}
          >
            {({ values, errors, touched, handleChange, handleBlur, handleSubmit, setFieldValue }) => (
              <form onSubmit={handleSubmit}>
                {Object.keys(formValues).map(val=>errors[val] && touched[val] && <Alert severity="error" sx={{mb: 2}}>{errors[val]}</Alert>)}
                
                {/* ── Section 1: Customer Info ── */}
                <Typography sx={{ fontWeight: 700, fontSize: 12, color: 'var(--accent-green)', textTransform: 'uppercase', letterSpacing: 1, mb: 1 }}>
                  1. Customer Details
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <PremiumTextField
                      id="name"
                      label="Account Name"
                      type="text"
                      required
                      value={values.name}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <PremiumTextField
                      id="contact"
                      label="Mobile No."
                      type="tel"
                      value={values.contact}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      inputProps={{ maxLength: 10, inputMode: 'numeric' }}
                    />
                  </Grid>
                </Grid>

                <Box sx={{ mt: 3, mb: 3, borderTop: '1px solid rgba(255,255,255,0.05)' }} />

                {/* ── Section 2: Loan Details ── */}
                <Typography sx={{ fontWeight: 700, fontSize: 12, color: 'var(--accent-green)', textTransform: 'uppercase', letterSpacing: 1, mb: 1 }}>
                  2. Loan Details
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <PremiumTextField 
                      id="date"
                      label="Disbursement Date"
                      type="date"
                      required
                      value={values.date}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <PremiumTextField
                      id="amount"
                      label="Principal Amount"
                      type="number"
                      required
                      value={values.amount}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      inputProps={{ inputMode: 'decimal' }}
                      InputProps={{
                        startAdornment: <InputAdornment position="start" sx={{'& p':{color:'var(--text-muted)'}}}>₹</InputAdornment>,
                      }}
                    />
                    <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      {[5000, 10000, 20000, 50000].map(amt => (
                        <Chip 
                          key={amt} 
                          label={`₹${amt/1000}k`} 
                          size="small" 
                          onClick={() => {
                            handleChange({ target: { name: 'amount', value: amt } });
                            if (values.interestPercentage) {
                              handleChange({ target: { name: 'interestAmount', value: Math.round(amt * (values.interestPercentage / 100)) } });
                            }
                          }}
                          sx={{ 
                            background: 'var(--bg-elevated)', color: 'var(--text-secondary)', 
                            border: '1px solid var(--border-subtle)',
                            '&:hover': { background: 'rgba(21, 195, 88, 0.1)', borderColor: 'var(--accent-green)', color: 'var(--accent-green)' }
                          }}
                        />
                      ))}
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <PremiumTextField
                      id="interestPercentage"
                      name="interestPercentage"
                      label="Interest %"
                      type="number"
                      required
                      value={values.interestPercentage}
                      onChange={(e)=>{
                        handleChange(e);
                        const calculatedInterest = Math.round(Number(values.amount || 0) * (e.target.value / 100));
                        values.interestAmount = calculatedInterest;
                        if (values.installmentPeriod) {
                           const total = values.collectUpfrontInterest ? Number(values.amount || 0) : Number(values.amount || 0) + calculatedInterest;
                           values.installmentAmount = Math.round(total / values.installmentPeriod);
                        }
                      }}
                      onBlur={handleBlur}
                      inputProps={{ inputMode: 'decimal' }}
                      InputProps={{
                        endAdornment: <InputAdornment position="end" sx={{'& p':{color:'var(--text-muted)'}}}>%</InputAdornment>,
                      }}
                    />
                    <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      {[5, 10, 12, 15].map(pct => (
                        <Chip 
                          key={pct} 
                          label={`${pct}%`} 
                          size="small" 
                          onClick={() => {
                            handleChange({ target: { name: 'interestPercentage', value: pct } });
                            if (values.amount) {
                              const calculatedInterest = Math.round(values.amount * (pct / 100));
                              handleChange({ target: { name: 'interestAmount', value: calculatedInterest } });
                              
                              if (values.installmentPeriod) {
                                const total = values.collectUpfrontInterest ? Number(values.amount || 0) : Number(values.amount || 0) + calculatedInterest;
                                handleChange({ target: { name: 'installmentAmount', value: Math.round(total / values.installmentPeriod) } });
                              }
                            }
                          }}
                          sx={{ 
                            background: 'var(--bg-elevated)', color: 'var(--text-secondary)', 
                            border: '1px solid var(--border-subtle)',
                            '&:hover': { background: 'rgba(21, 195, 88, 0.1)', borderColor: 'var(--accent-green)', color: 'var(--accent-green)' }
                          }}
                        />
                      ))}
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <PremiumTextField
                      id="interestAmount"
                      label="Interest Amount"
                      type="number"
                      value={values.interestAmount}
                      onChange={(e) => {
                        handleChange(e);
                        if (values.installmentPeriod) {
                           const total = values.collectUpfrontInterest ? Number(values.amount || 0) : Number(values.amount || 0) + Number(e.target.value || 0);
                           values.installmentAmount = Math.round(total / values.installmentPeriod);
                        }
                      }}
                      onBlur={handleBlur}
                      inputProps={{ inputMode: 'decimal' }}
                      InputProps={{
                        startAdornment: <InputAdornment position="start" sx={{'& p':{color:'var(--text-muted)'}}}>₹</InputAdornment>,
                      }}
                    />
                  </Grid>
                </Grid>
                
                {values.interestAmount > 0 && (
                  <Box sx={{ mt: 1, ml: 1 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={values.collectUpfrontInterest}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            handleChange(e);
                            const total = checked ? Number(values.amount || 0) : Number(values.amount || 0) + Number(values.interestAmount || 0);
                            if (values.installmentPeriod > 0) {
                              setFieldValue('installmentAmount', Math.round(total / values.installmentPeriod));
                            }
                          }}
                          name="collectUpfrontInterest"
                          sx={{ color: 'var(--accent-green)', '&.Mui-checked': { color: 'var(--accent-green)' } }}
                        />
                      }
                      label={<Typography sx={{ fontSize: 14, fontFamily: 'Inter', color: 'var(--text-secondary)' }}>Record upfront interest as collected today?</Typography>}
                    />
                  </Box>
                )}

                <Box sx={{ mt: 3, mb: 3, borderTop: '1px solid rgba(255,255,255,0.05)' }} />

                {/* ── Section 3: Repayment Plan ── */}
                <Typography sx={{ fontWeight: 700, fontSize: 12, color: 'var(--accent-green)', textTransform: 'uppercase', letterSpacing: 1, mb: 1 }}>
                  3. Repayment Plan
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography sx={{ fontSize: 12, color: 'var(--text-muted)', fontFamily: 'Inter', mb: 1, ml: 1 }}>Frequency</Typography>
                    <ToggleButtonGroup
                      color="primary"
                      value={values.installmentType}
                      exclusive
                      onChange={(e, newAlignment) => {
                        if (newAlignment !== null) {
                          handleChange({ target: { name: 'installmentType', value: newAlignment } });
                          const conversion = { daily: 1, weekly: 7, monthly: 30 };
                          const oldDays = 100;
                          const newPeriod = Math.max(1, Math.round(oldDays / conversion[newAlignment]));
                          setFieldValue('installmentPeriod', newPeriod);
                          
                          const total = values.collectUpfrontInterest ? Number(values.amount || 0) : Number(values.amount || 0) + Number(values.interestAmount || 0);
                          if (newPeriod > 0) {
                            setFieldValue('installmentAmount', Math.round(total / newPeriod));
                          }
                        }
                      }}
                      fullWidth
                      sx={{
                        display: 'flex',
                        width: '100%',
                        '& .MuiToggleButtonGroup-grouped': { flex: 1 },
                        '& .MuiToggleButton-root': {
                          fontFamily: 'Inter',
                          fontWeight: 600,
                          textTransform: 'none',
                          borderColor: 'var(--border-subtle)',
                          color: 'var(--text-secondary)',
                          py: 1,
                          '&.Mui-selected': {
                            backgroundColor: 'rgba(21, 195, 88, 0.1)',
                            color: 'var(--accent-green)',
                            borderColor: 'var(--accent-green)'
                          }
                        }
                      }}
                    >
                      <ToggleButton value="daily" sx={{ flex: 1 }}>Daily</ToggleButton>
                      <ToggleButton value="weekly" sx={{ flex: 1 }}>Weekly</ToggleButton>
                      <ToggleButton value="monthly" sx={{ flex: 1 }}>Monthly</ToggleButton>
                    </ToggleButtonGroup>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <PremiumTextField
                      id="installmentPeriod"
                      name="installmentPeriod"
                      label="Duration"
                      type="number"
                      required
                      value={values.installmentPeriod}
                      onChange={(e)=>{
                        handleChange(e);
                        const period = Number(e.target.value);
                        const total = values.collectUpfrontInterest ? Number(values.amount || 0) : Number(values.amount || 0) + Number(values.interestAmount || 0);
                        if (period > 0) {
                          setFieldValue('installmentAmount', Math.round(total / period));
                        }
                      }}
                      onBlur={handleBlur}
                      inputProps={{ inputMode: 'numeric' }}
                      InputProps={{
                        endAdornment: <InputAdornment position="end" sx={{'& p':{color:'var(--text-muted)'}}}>
                          {values.installmentType === "daily" ? "days" : values.installmentType === "monthly" ? "mos" : "wks"}
                        </InputAdornment>,
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <PremiumTextField
                      id="installmentAmount"
                      name="installmentAmount"
                      label="Installment Size"
                      type="number"
                      required
                      value={values.installmentAmount}
                      onChange={(e)=>{
                        handleChange(e);
                        const newAmt = Number(e.target.value);
                        const total = values.collectUpfrontInterest ? Number(values.amount || 0) : Number(values.amount || 0) + Number(values.interestAmount || 0);
                        if (newAmt > 0 && total > 0) {
                          setFieldValue('installmentPeriod', Math.ceil(total / newAmt));
                        }
                      }}
                      onBlur={handleBlur}
                      inputProps={{ inputMode: 'decimal' }}
                      InputProps={{
                        startAdornment: <InputAdornment position="start" sx={{'& p':{color:'var(--text-muted)'}}}>₹</InputAdornment>,
                      }}
                    />
                  </Grid>
                </Grid>

                {/* ── Live Summary Card ── */}
                {(Number(values.amount) > 0 || Number(values.interestAmount) > 0) && (
                  <Box sx={{ 
                    mt: 4, mb: 2, p: 2, borderRadius: 3, 
                    background: 'linear-gradient(135deg, rgba(21, 195, 88, 0.1), rgba(21, 195, 88, 0.05))',
                    border: '1px solid rgba(21, 195, 88, 0.2)',
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2
                  }}>
                    <Box>
                      <Typography sx={{ fontSize: 11, color: 'var(--accent-green)', textTransform: 'uppercase', fontWeight: 700, letterSpacing: 0.5 }}>
                        {values.collectUpfrontInterest ? "To Recover (Principal Only)" : "Total Loan Value"}
                      </Typography>
                      <Typography sx={{ fontSize: 18, fontWeight: 800, color: 'var(--text-primary)', fontFamily: 'Inter' }}>
                        ₹{(values.collectUpfrontInterest ? Number(values.amount || 0) : Number(values.amount || 0) + Number(values.interestAmount || 0)).toLocaleString('en-IN')}
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'right' }}>
                      <Typography sx={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', fontWeight: 700, letterSpacing: 0.5 }}>
                        {values.installmentType} Schedule
                      </Typography>
                      <Typography sx={{ fontSize: 15, fontWeight: 600, color: 'var(--text-secondary)', fontFamily: 'Inter' }}>
                        ₹{Number(values.installmentAmount || 0).toLocaleString('en-IN')} × {values.installmentPeriod || 0}
                      </Typography>
                    </Box>
                  </Box>
                )}

                <DialogActions sx={{ pt: 3, px: 0, pb: 0, borderTop: '1px solid var(--border-subtle)', mt: 3 }}>
                    <Button 
                      onClick={handleClose} 
                      sx={{ color: 'var(--text-muted)', fontFamily: 'Inter', fontWeight: 600, px: 3 }}
                    >
                      Cancel
                    </Button>
                    <Button 
                      type="submit" 
                      variant="contained" 
                      onClick={handleSubmit}
                      sx={{ 
                        fontFamily: 'Inter', fontWeight: 700, px: 4, py: 1, borderRadius: 2,
                        background: 'linear-gradient(135deg, #15C358, #109441)',
                        color: '#fff',
                        boxShadow: '0 4px 14px rgba(21, 195, 88, 0.4)',
                        '&:hover': { background: '#109441' }
                      }}
                    >
                      Save Account
                    </Button>
                </DialogActions>
              </form>
            )}
          </Formik>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default AddAccountForm;
